import React, { useState, useMemo } from 'react';
import { DataRetentionRecordUI } from '../dataTypeMapping';
import AssetsSuppliersCell from './AssetsSuppliersCell';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface DataRetentionTableCardsProps {
  records: DataRetentionRecordUI[];
  onView: (record: DataRetentionRecordUI) => void;
  pageSize?: number;
  pageIndex?: number;
  onPageIndexChange?: (pageIndex: number) => void;
}

const DataRetentionTableCards: React.FC<DataRetentionTableCardsProps> = ({ 
  records, 
  onView, 
  pageSize: initialPageSize = 5,
  pageIndex: controlledPageIndex,
  onPageIndexChange
}) => {
  const [internalPageIndex, setInternalPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  
  // Use controlled pageIndex if provided, otherwise use internal state
  const currentPage = controlledPageIndex !== undefined ? controlledPageIndex : internalPageIndex;
  const setCurrentPage = onPageIndexChange || setInternalPageIndex;

  const pageCount = Math.ceil(records.length / pageSize);
  const canPreviousPage = currentPage > 0;
  const canNextPage = currentPage < pageCount - 1;

  // Reset to page 0 when records change (filter applied)
  React.useEffect(() => {
    if (controlledPageIndex === undefined) {
      setInternalPageIndex(0);
    }
  }, [records.length, controlledPageIndex]);

  const currentPageRecords = useMemo(() => {
    const start = currentPage * pageSize;
    return records.slice(start, start + pageSize);
  }, [records, currentPage, pageSize]);

  const handlePageSizeChange = (value: string) => {
    const newPageSize = Number(value);
    setPageSize(newPageSize);
    // Reset to first page when changing page size
    setCurrentPage(0);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-4 w-full">
        {currentPageRecords.length > 0 ? (
          currentPageRecords.map((record, idx) => (
            <div key={record.dataTypeID || idx} className="rounded-lg shadow bg-white p-4 flex flex-col gap-2 border border-muted">
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-base">{record.dataTypeName}</span>
                <Button size="sm" variant="outline" onClick={() => onView(record)}>View</Button>
              </div>
              <div className="text-xs text-muted-foreground mb-1">{record.dataTypeJustification}</div>
              <div className="flex flex-row flex-wrap gap-2 text-sm">
                <div><span className="font-semibold">Retention:</span> {record.dataRetentionPeriodMin} - {record.dataRetentionPeriodMax} years</div>
                <div><span className="font-semibold">Legal:</span> {record.dataTypeModeOfCollection}</div>
                <div><span className="font-semibold">Status:</span> <span style={{ color: record.status === 'Active' ? 'green' : 'gray', fontWeight: 500 }}>{record.status}</span></div>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-xs">Assets/Suppliers:</span>
                <AssetsSuppliersCell items={record.assets || []} />
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4 text-muted-foreground">No records found</div>
        )}
      </div>

      <div className="flex flex-col gap-2 pt-2 border-t">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">Rows</p>
            <Select
              value={`${pageSize}`}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent>
                {[5, 10, 20, 30, 50].map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentPage(0)}
              disabled={!canPreviousPage}
              className="h-8 w-8 p-0"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                const newPage = Math.max(0, currentPage - 1);
                setCurrentPage(newPage);
              }}
              disabled={!canPreviousPage}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="px-1 text-sm text-muted-foreground">
              {currentPage + 1}/{Math.max(1, pageCount)}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                const newPage = Math.min(pageCount - 1, currentPage + 1);
                setCurrentPage(newPage);
              }}
              disabled={!canNextPage}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentPage(pageCount - 1)}
              disabled={!canNextPage}
              className="h-8 w-8 p-0"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataRetentionTableCards;
